import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import {
  adminFetch,
  adminUpload,
  AdminUnauthorized,
  getAdminToken,
} from './admin-api';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;

const fmt = (v: Any) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const DEFAULTS = {
  name: '',
  display_name: '',
  description: '',
  cost: 100000,
  item_order: 1,
  visible: true,
  profit_items: [],
};

// Слоты картинок опции (см. бэкенд ConfigurationItem). main_image — по умолчанию,
// остальные показываются для конкретной модели в модалке ⓘ.
const IMAGE_SLOTS: Array<[string, string]> = [
  ['main_image', 'Основная (по умолчанию)'],
  ['image_16', 'БП16/20'],
  ['image_25_g', 'БП25/31 габарит'],
  ['image_25', 'БП25/31 негабарит'],
  ['image_33', 'БП33/42'],
  ['image_40', 'БП40/50'],
];

export const AdminOptionsView = () => {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Any[]>([]);
  const [categories, setCategories] = useState<Any[]>([]);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<Any | null>(null);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  // Картинки опции (ActiveStorage → Cloudinary). Доступно только у сохранённой опции.
  const syncOption = (updated: Any) => {
    setEditing(updated);
    setOptions((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
  };

  const uploadImage = async (slot: string, file: File) => {
    if (!editing?.id) return;
    setImgBusy(true);
    try {
      syncOption(
        await adminUpload(
          `/admin/configuration_items/${editing.id}/image`,
          file,
          { slot }
        )
      );
      message.success('Картинка обновлена');
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setImgBusy(false);
    }
  };

  const removeImage = async (slot: string) => {
    if (!editing?.id) return;
    setImgBusy(true);
    try {
      syncOption(
        await adminFetch(
          `/admin/configuration_items/${editing.id}/image?slot=${slot}`,
          { method: 'DELETE' }
        )
      );
      message.success('Картинка удалена');
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setImgBusy(false);
    }
  };

  const load = async (token?: string) => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([
        adminFetch('/admin/configuration_items', { token }),
        adminFetch('/admin/configuration_items/categories', { token }),
      ]);
      setOptions(o || []);
      setCategories(c || []);
      setAuthed(true);
      if (token) localStorage.setItem('admin_token', token);
    } catch (e) {
      if (e instanceof AdminUnauthorized) {
        message.error('Неверный пароль администратора');
        localStorage.removeItem('admin_token');
        setAuthed(false);
      } else {
        message.error('Не удалось загрузить опции');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getAdminToken()) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (record?: Any) => {
    setEditing(record || {});
    form.setFieldsValue(
      record
        ? record
        : { ...DEFAULTS, category_id: categories[0]?.id }
    );
  };

  const save = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      profit_items: (values.profit_items || [])
        .map((s: string) => (s || '').trim())
        .filter(Boolean),
    };
    setSaving(true);
    try {
      const path = editing?.id
        ? `/admin/configuration_items/${editing.id}`
        : '/admin/configuration_items';
      await adminFetch(path, {
        method: editing?.id ? 'PATCH' : 'POST',
        body: { configuration_item: payload },
      });
      message.success('Опция сохранена');
      setEditing(null);
      await load();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await adminFetch(`/admin/configuration_items/${id}`, { method: 'DELETE' });
      message.success('Опция удалена');
      await load();
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  if (!authed) {
    return (
      <div className="container max-w-[420px] py-16">
        <h2 className="text-[20px] font-semibold mb-4">Справочник опций</h2>
        <Input.Password
          placeholder="Пароль администратора"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          onPressEnter={() => load(pwd)}
          className="mb-3"
        />
        <Button type="primary" block loading={loading} onClick={() => load(pwd)}>
          Войти
        </Button>
      </div>
    );
  }

  const columns = [
    { title: 'Опция', dataIndex: 'display_name' },
    {
      title: 'Системное имя',
      dataIndex: 'name',
      render: (v: string) => <span className="text-[#888]">{v}</span>,
    },
    {
      title: 'Доплата, ₽',
      dataIndex: 'cost',
      render: (v: number) => fmt(v),
    },
    {
      title: 'Видимость',
      dataIndex: 'visible',
      render: (v: boolean) =>
        v ? <Tag color="green">видна</Tag> : <Tag>скрыта</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_: Any, o: Any) => (
        <div className="flex gap-2">
          <Button size="small" onClick={() => open(o)}>
            Изменить
          </Button>
          <Popconfirm
            title="Удалить опцию?"
            description="Удалит правила совместимости по этой опции во всех модификациях."
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={() => remove(o.id)}>
            <Button size="small" danger>
              Удалить
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="container pb-[120px]">
      <div className="flex items-center justify-between my-6">
        <h2 className="text-[22px] font-semibold">Справочник опций</h2>
        <Link to="/admin/catalog" className="text-[#007E3C] underline">
          ← Модели и модификации
        </Link>
      </div>

      <Button
        type="primary"
        className="mb-4"
        style={{ background: '#66B28A' }}
        onClick={() => open()}>
        + Добавить опцию
      </Button>

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          pagination={false}
          columns={columns}
          dataSource={options}
        />
      </Spin>

      <Modal
        title={editing?.id ? 'Изменить опцию' : 'Новая опция'}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={save}
        confirmLoading={saving}
        okText="Сохранить"
        cancelText="Отмена"
        width={620}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="display_name"
            label="Название (для клиента)"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Системное имя (латиницей, уникальное)"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <Input placeholder="hydraulic_slide_gates" />
          </Form.Item>
          <Form.Item name="category_id" label="Категория" rules={[{ required: true }]}>
            <Select
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item
            name="cost"
            label="Доплата, ₽"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <InputNumber min={1} step={1000} formatter={fmt} className="w-full" />
          </Form.Item>
          <Form.Item name="item_order" label="Порядок" rules={[{ required: true }]}>
            <InputNumber min={1} className="w-full" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item label="Польза (буллеты в модалке ⓘ)">
            <Form.List name="profit_items">
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-2">
                  {fields.map((field) => (
                    <div key={field.key} className="flex gap-2">
                      <Form.Item {...field} noStyle>
                        <Input placeholder="Например: ускоряет перегрузку" />
                      </Form.Item>
                      <Button danger onClick={() => remove(field.name)}>
                        ✕
                      </Button>
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add('')}>
                    + Добавить пункт
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          {editing?.id ? (
            <Form.Item label="Картинки (для модалки ⓘ)">
              <div className="grid grid-cols-2 gap-3">
                {IMAGE_SLOTS.map(([slot, label]) => {
                  const url = editing.images?.[slot];
                  return (
                    <div key={slot} className="border rounded p-2">
                      <div className="text-[13px] mb-1">{label}</div>
                      {url ? (
                        <img
                          src={url}
                          alt=""
                          className="w-full h-[90px] object-contain bg-[#fafafa] mb-1"
                        />
                      ) : (
                        <div className="w-full h-[90px] flex items-center justify-center text-[#bbb] bg-[#fafafa] mb-1">
                          нет
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Upload
                          accept="image/png,image/jpeg"
                          showUploadList={false}
                          beforeUpload={(file) => {
                            uploadImage(slot, file as File);
                            return false;
                          }}>
                          <Button size="small" loading={imgBusy}>
                            Загрузить
                          </Button>
                        </Upload>
                        {url && (
                          <Button
                            size="small"
                            danger
                            loading={imgBusy}
                            onClick={() => removeImage(slot)}>
                            Удалить
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="text-[12px] text-[#999] mt-1">
                PNG/JPEG, до 5 МБ. «Основная» показывается, если для модели нет
                отдельной.
              </div>
            </Form.Item>
          ) : (
            <div className="mb-4 text-[12px] text-[#999]">
              Картинки можно загрузить после сохранения опции.
            </div>
          )}

          <Form.Item name="visible" label="Видна клиенту" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
